// Height of the native control bar of <video controls> (seek bar, volume, fullscreen) in browsers
export const MEDIA_CONTROLS_HEIGHT = 60;

const getPointerY = (event: Event): number | undefined => {
  const touchEvent = event as TouchEvent;
  if (touchEvent.touches || touchEvent.changedTouches) {
    const touch = touchEvent.touches?.[0] ?? touchEvent.changedTouches?.[0];
    return touch?.clientY;
  }
  return (event as MouseEvent).clientY;
};

/**
 * True when a gesture starts on the native control bar of a video/audio element.
 * Dragging the seek bar or the volume slider there must not move the slide.
 */
export function isOnMediaControls(event: Event | undefined | null): boolean {
  const target = event?.target;
  if (!(target instanceof HTMLMediaElement) || !target.controls) return false;
  const pointerY = getPointerY(event as Event);
  if (pointerY === undefined) return false;
  const rect = target.getBoundingClientRect();
  return pointerY >= rect.bottom - MEDIA_CONTROLS_HEIGHT;
}

/**
 * Swipe (touch) and drag (mouse) between items is allowed on every device,
 * except while the item is zoomed (the gesture pans the image), while the viewer is locked
 * (stereo player is active) and when the gesture starts on the media controls.
 */
export function canSwipe(options: { locked: boolean; zoomScale?: number; event?: Event | null }): boolean {
  if (options.locked) return false;
  if ((options.zoomScale ?? 1) > 1) return false;
  return !isOnMediaControls(options.event);
}

/**
 * Thumbnail strip of the full screen viewer: shown only when there is something to choose from.
 */
export function showThumbnails(options: { isFullScreen: boolean; itemsCount: number; locked: boolean }): boolean {
  return options.isFullScreen && options.itemsCount > 1 && !options.locked;
}

/**
 * scrollLeft of the thumbnail strip that puts the given thumbnail in the middle of the strip,
 * clamped to the scrollable range.
 */
export function getThumbnailScrollLeft(options: {
  stripWidth: number;
  scrollWidth: number;
  thumbnailLeft: number;
  thumbnailWidth: number;
}): number {
  const { stripWidth, scrollWidth, thumbnailLeft, thumbnailWidth } = options;
  const maxScroll = Math.max(0, scrollWidth - stripWidth);
  const centered = thumbnailLeft - (stripWidth - thumbnailWidth) / 2;
  return Math.round(Math.min(maxScroll, Math.max(0, centered)));
}
